import {
  ChevronDown,
  ChevronRight,
  LogOut,
  Menu,
  MousePointerClick,
  PanelLeft,
  PanelLeftClose,
  PanelLeftDashed,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SimpleTooltip } from '@/components/ui/tooltip';
import {
  CONSOLE_SIDEBAR_MODE_KEY,
  persistSidebarMode,
  readSidebarMode,
  type SidebarMode,
} from '@/hooks/useSidebarMode';
import { cn } from '@/lib/utils';
import { LogoMark, Wordmark } from './logo';
import { SELECTION_BAR } from './selection-bar';

/**
 * AppSidebar: the app-shell rail. Ported from KnitKnot's shared AppSidebar.
 *
 * What stays HERE: geometry, modes (expanded / collapsed / expand-on-hover,
 * persisted), the ⌘B mode cycle, collapsed-rail tooltips, the active-item
 * treatment, nav groups, section labels, and the mobile drawer. What stays at
 * the CALL SITE (console-sidebar.tsx): which entries exist, which one is
 * active, badge counts, and sign-out.
 *
 * Widths come from theme.css (`--app-sidebar-*-w`), the single source of
 * truth. Never reintroduce a px literal or a TS constant for them.
 */

const EXPANDED_W = 'var(--app-sidebar-expanded-w)';
const COLLAPSED_W = 'var(--app-sidebar-collapsed-w)';
const HOVER_COLLAPSE_DELAY = 100;

// ─── Shared link classes ────────────────────────────────────────
// One source of truth for item styling, so expanded and collapsed never
// drift. `focus-ring` is the canonical keyboard focus treatment; without it
// the primary navigation is invisible to keyboard users. Do not drop it.
const NAV_LINK_BASE =
  'focus-ring group relative flex h-8 items-center gap-2.5 rounded-sm px-[11px] text-sm transition-colors duration-150';
const NAV_LINK_ACTIVE = 'bg-sidebar-accent text-sidebar-accent-foreground';
const NAV_LINK_INACTIVE = 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground';
const NAV_ICON_CONTAINER = 'relative flex size-4 shrink-0 items-center justify-center';
const NAV_ICON_ACTIVE = 'size-4 text-ink';
const NAV_ICON_INACTIVE = 'size-4 text-ink-3 transition-colors group-hover:text-ink';
// The rail has 10px of side padding, so -10px puts the bar on the rail's edge.
const ACTIVE_BAR = cn(SELECTION_BAR, 'top-1/2 h-4 -translate-y-1/2 -left-[10px]');

export interface AppNavItem {
  key: string;
  title: string;
  /** For an external item this is the FINAL href; the adapter resolves it. */
  href: string;
  icon?: ComponentType<{ className?: string }>;
  isExternal?: boolean;
  /** External links open a new tab unless set to false. */
  newTab?: boolean;
  /** Pinned to the bottom of the rail, outside the scrollable area. */
  bottom?: boolean;
  /** Count badge (expanded) / dot (collapsed), shown only when > 0. */
  badge?: number;
}

interface AppNavSection {
  type: 'section';
  /** An empty title renders a divider rather than a label. */
  title: string;
}

export interface AppNavGroup {
  type: 'group';
  key: string;
  title: string;
  icon: ComponentType<{ className?: string }>;
  children: AppNavItem[];
}

export type AppNavEntry = AppNavSection | AppNavGroup | AppNavItem;

function isAppNavItem(entry: AppNavEntry): entry is AppNavItem {
  return !('type' in entry);
}

function isAppNavGroup(entry: AppNavEntry): entry is AppNavGroup {
  return 'type' in entry && entry.type === 'group';
}

interface AppSidebarProps {
  entries: AppNavEntry[];
  /** Longest-prefix (or app-specific) active test. External items never ask. */
  isActive: (href: string) => boolean;
  onSignOut: () => void;
  /** Collapsed-rail mark. */
  logo?: ReactNode;
  /** Expanded-rail and mobile wordmark. */
  wordmark?: ReactNode;
  /** Where the logo links. */
  homeHref?: string;
  /** Accessible name for the rail. */
  ariaLabel?: string;
  /** Temporarily render a mode without replacing the saved choice (focused
   *  flows that need the content width back). */
  forcedMode?: SidebarMode;
  /** Resting mode when there is no saved preference. */
  defaultMode?: SidebarMode;
  /** Preference register. Keep in sync with the pre-paint script in index.html. */
  modeStorageKey?: string;
}

export function AppSidebar({
  entries,
  isActive,
  onSignOut,
  logo = <LogoMark className="text-ink" />,
  wordmark = <Wordmark className="text-ink" />,
  homeHref = '/',
  ariaLabel = 'Main navigation',
  forcedMode,
  defaultMode = 'expanded',
  modeStorageKey = CONSOLE_SIDEBAR_MODE_KEY,
}: AppSidebarProps) {
  const mainNav = useMemo(() => entries.filter((e) => (isAppNavItem(e) ? !e.bottom : true)), [entries]);
  const bottomNav = useMemo(() => entries.filter((e): e is AppNavItem => isAppNavItem(e) && !!e.bottom), [entries]);

  const [mode, setMode] = useState<SidebarMode>(() => readSidebarMode(modeStorageKey, defaultMode));
  const [isHovered, setIsHovered] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveMode = forcedMode ?? mode;
  const isExpanded = effectiveMode === 'expanded' || (effectiveMode === 'hover' && isHovered);
  const isHoverExpanded = effectiveMode === 'hover' && isHovered;

  // --app-content-inset drives the LAYOUT. Content is pushed only for the
  // CHOSEN mode, never for the hover preview: a hover-expanded rail floats
  // over the content, because pushing on hover jiggles text on every stray
  // mouse move. The mode→inset mapping lives in theme.css; this effect writes
  // ONLY the attribute, so it cannot disagree with the pre-paint script.
  useEffect(() => {
    document.documentElement.setAttribute('data-sidebar-mode', effectiveMode);
  }, [effectiveMode]);

  // Persist only the mode the user CHOSE, never a forced presentation.
  useEffect(() => {
    persistSidebarMode(mode, modeStorageKey);
  }, [mode, modeStorageKey]);

  const changeMode = useCallback((next: SidebarMode) => {
    setMode(next);
    if (next !== 'hover') setIsHovered(false);
  }, []);

  // ⌘B / Ctrl+B cycles expanded → hover → collapsed.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (forcedMode) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
        e.preventDefault();
        setIsHovered(false);
        setMode((prev) => {
          const cycle: SidebarMode[] = ['expanded', 'hover', 'collapsed'];
          return cycle[(cycle.indexOf(prev) + 1) % cycle.length];
        });
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [forcedMode]);

  // Escape closes the mobile drawer.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  const handleMouseEnter = useCallback(() => {
    if (effectiveMode !== 'hover') return;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
  }, [effectiveMode]);

  const handleMouseLeave = useCallback(() => {
    if (effectiveMode !== 'hover') return;
    hoverTimeoutRef.current = setTimeout(() => setIsHovered(false), HOVER_COLLAPSE_DELAY);
  }, [effectiveMode]);

  useEffect(
    () => () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    },
    [],
  );

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      {/* Brand block tracks the rail width, so the wordmark and the nav share
          one left edge. It occupies the top bar's left gutter. */}
      <Link
        to={homeHref}
        aria-label="Latisent home"
        className={cn(
          'focus-ring fixed left-0 top-0 z-shell-logo hidden items-center overflow-hidden border-b border-r border-sidebar-border bg-sidebar px-[18px] transition-[width] duration-200 ease-out md:flex',
          isExpanded ? 'justify-start' : 'justify-center px-0',
        )}
        style={{ width: isExpanded ? EXPANDED_W : COLLAPSED_W, height: 'var(--app-topnav-h)' }}
      >
        {isExpanded ? wordmark : logo}
      </Link>

      {/* Desktop rail, below the logo. Its visual width tracks isExpanded (so
          hover grows the panel); the content inset tracks mode only (so hover
          floats instead of pushing). */}
      <nav
        aria-label={ariaLabel}
        className={cn(
          'fixed bottom-0 left-0 z-shell hidden flex-col overflow-hidden border-r border-sidebar-border bg-sidebar transition-[width,box-shadow] duration-200 ease-out md:flex',
          isHoverExpanded && 'shadow-xl',
        )}
        style={{ top: 'var(--app-topnav-h)', width: isExpanded ? EXPANDED_W : COLLAPSED_W }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className="flex-1 overflow-y-auto overflow-x-hidden px-2.5 pb-2 pt-3">
          <div className="flex flex-col gap-px">
            {mainNav.map((entry, i) =>
              isAppNavItem(entry) ? (
                <NavLink
                  key={entry.key}
                  item={entry}
                  isActive={!entry.isExternal && isActive(entry.href)}
                  isExpanded={isExpanded}
                />
              ) : isAppNavGroup(entry) ? (
                <DesktopNavGroup key={entry.key} group={entry} isExpanded={isExpanded} isActive={isActive} />
              ) : (
                <SectionLabel key={`section-${i}`} title={entry.title} isExpanded={isExpanded} />
              ),
            )}
          </div>
        </div>

        <div className="flex flex-col gap-px px-2.5 pb-3 pt-2">
          {bottomNav.map((item) => (
            <NavLink
              key={item.key}
              item={item}
              isActive={!item.isExternal && isActive(item.href)}
              isExpanded={isExpanded}
            />
          ))}
          {!forcedMode && (
            <>
              <div className="mx-1.5 my-1.5 border-t border-sidebar-border" />
              <SidebarControl mode={mode} onChangeMode={changeMode} isExpanded={isExpanded} />
            </>
          )}
        </div>
      </nav>

      {/* Mobile: the drawer button sits in the top bar's left gutter (AppShell
          pads its contents to leave room for it). */}
      <div
        className="fixed left-0 top-0 z-mobile-bar flex items-center pl-2 md:hidden"
        style={{ height: 'var(--app-topnav-h)' }}
      >
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          className="focus-ring flex size-10 items-center justify-center rounded-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-ink"
          aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={mobileOpen}
          aria-controls="mobile-nav"
        >
          <Menu className="size-5" aria-hidden="true" />
        </button>
      </div>

      {/* Mobile drawer: pure CSS enter/exit, always mounted so the exit
          transition plays; `inert` takes it out of the tab order when shut. */}
      <div
        className={cn(
          'fixed inset-0 z-mobile-scrim bg-night transition-opacity duration-300 md:hidden',
          mobileOpen ? 'opacity-40' : 'pointer-events-none opacity-0',
        )}
        aria-hidden="true"
        onClick={closeMobile}
      />
      <nav
        id="mobile-nav"
        aria-label="Mobile navigation"
        inert={!mobileOpen}
        aria-hidden={!mobileOpen}
        className={cn(
          'fixed left-0 top-0 z-mobile-panel flex h-full w-72 max-w-[85vw] flex-col border-r border-sidebar-border bg-sidebar md:hidden',
          'transition-transform duration-200 ease-out motion-reduce:transition-none',
          mobileOpen ? 'translate-x-0 shadow-xl' : '-translate-x-full',
        )}
      >
        <div
          className="flex items-center justify-between border-b border-sidebar-border pl-4 pr-2"
          style={{ height: 'var(--app-topnav-h)' }}
        >
          <Link to={homeHref} onClick={closeMobile} className="focus-ring rounded-sm">
            {wordmark}
          </Link>
          <button
            type="button"
            onClick={closeMobile}
            className="focus-ring flex size-10 items-center justify-center rounded-sm text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
            aria-label="Close navigation menu"
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3">
          <div className="flex flex-col gap-px">
            {mainNav.map((entry, i) =>
              isAppNavItem(entry) ? (
                <MobileNavLink
                  key={entry.key}
                  item={entry}
                  isActive={!entry.isExternal && isActive(entry.href)}
                  onNavigate={closeMobile}
                />
              ) : isAppNavGroup(entry) ? (
                <MobileNavGroup key={entry.key} group={entry} isActive={isActive} onNavigate={closeMobile} />
              ) : entry.title ? (
                <div key={`section-${i}`} className="px-3 pb-1 pt-4">
                  <span className="text-micro font-medium text-ink-3">{entry.title}</span>
                </div>
              ) : (
                <div key={`section-${i}`} className="mx-3 my-2 border-t border-sidebar-border" />
              ),
            )}
          </div>
        </div>
        <div className="flex flex-col gap-px border-t border-sidebar-border px-2 py-3">
          {bottomNav.map((item) => (
            <MobileNavLink
              key={item.key}
              item={item}
              isActive={!item.isExternal && isActive(item.href)}
              onNavigate={closeMobile}
            />
          ))}
          <button
            type="button"
            onClick={() => {
              closeMobile();
              onSignOut();
            }}
            className="focus-ring flex h-10 w-full items-center gap-3 rounded-sm px-3 text-sm font-medium text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:text-ink"
          >
            <LogOut className="size-4 text-ink-3" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </nav>
    </>
  );
}

// ─── Navigation groups ─────────────────────────────────────────
// Expanded rails and the mobile drawer disclose children in place. A
// collapsed rail cannot show inline labels, so the same group opens as a
// right-hand flyout. Groups start closed unless they own the current route.

function useOpenWhenActive(active: boolean) {
  const [open, setOpen] = useState(active);
  // Adjust state during render (not in an effect) when the route moves into
  // the group, so it opens without a second paint.
  const [prevActive, setPrevActive] = useState(active);
  if (active !== prevActive) {
    setPrevActive(active);
    if (active) setOpen(true);
  }
  return [open, setOpen] as const;
}

function DesktopNavGroup({
  group,
  isExpanded,
  isActive,
}: {
  group: AppNavGroup;
  isExpanded: boolean;
  isActive: (href: string) => boolean;
}) {
  const active = group.children.some((item) => !item.isExternal && isActive(item.href));
  const [open, setOpen] = useOpenWhenActive(active);
  const Icon = group.icon;

  const trigger = (
    <button
      type="button"
      className={cn(NAV_LINK_BASE, active ? 'text-sidebar-accent-foreground' : NAV_LINK_INACTIVE, 'w-full', !isExpanded && 'justify-center px-0')}
      aria-label={group.title}
    >
      <span className={NAV_ICON_CONTAINER}>
        <Icon className={active ? NAV_ICON_ACTIVE : NAV_ICON_INACTIVE} />
      </span>
      {isExpanded && (
        <>
          <span className="flex-1 whitespace-nowrap text-left font-medium">{group.title}</span>
          <ChevronDown className={cn('size-3.5 text-ink-3 transition-transform', open && 'rotate-180')} aria-hidden="true" />
        </>
      )}
    </button>
  );

  if (!isExpanded) {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start" sideOffset={8} className="w-52">
          <DropdownMenuLabel>{group.title}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {group.children.map((item) => (
            <DropdownMenuItem key={item.key} asChild>
              {item.isExternal ? (
                <a href={item.href} target={item.newTab === false ? undefined : '_blank'} rel="noopener noreferrer">
                  {item.title}
                </a>
              ) : (
                <Link to={item.href} className={cn(isActive(item.href) && 'bg-surface-2 text-ink')}>
                  {item.title}
                </Link>
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>{trigger}</CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-px">
        {group.children.map((item) => (
          <NavLink key={item.key} item={item} isActive={!item.isExternal && isActive(item.href)} isExpanded nested />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

function MobileNavGroup({
  group,
  isActive,
  onNavigate,
}: {
  group: AppNavGroup;
  isActive: (href: string) => boolean;
  onNavigate: () => void;
}) {
  const active = group.children.some((item) => !item.isExternal && isActive(item.href));
  const [open, setOpen] = useOpenWhenActive(active);
  const Icon = group.icon;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'focus-ring flex h-10 w-full items-center gap-3 rounded-sm px-3 text-sm font-medium transition-colors',
            active ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
          )}
        >
          <Icon className="size-4 shrink-0 text-ink-3" />
          <span className="flex-1 text-left">{group.title}</span>
          <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} aria-hidden="true" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-px">
        {group.children.map((item) => (
          <MobileNavLink
            key={item.key}
            item={item}
            isActive={!item.isExternal && isActive(item.href)}
            onNavigate={onNavigate}
            nested
          />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ─── Sidebar control ────────────────────────────────────────────

const MODE_OPTIONS: { value: SidebarMode; label: string; icon: ComponentType<{ className?: string }> }[] = [
  { value: 'expanded', label: 'Expanded', icon: PanelLeft },
  { value: 'collapsed', label: 'Collapsed', icon: PanelLeftClose },
  { value: 'hover', label: 'Expand on hover', icon: MousePointerClick },
];

function SidebarControl({
  mode,
  onChangeMode,
  isExpanded,
}: {
  mode: SidebarMode;
  onChangeMode: (mode: SidebarMode) => void;
  isExpanded: boolean;
}) {
  const trigger = (
    // Identical layout to a nav item, so the rail's icons stay on one axis.
    <button
      type="button"
      className={cn(NAV_LINK_BASE, NAV_LINK_INACTIVE, 'w-full', !isExpanded && 'justify-center px-0')}
      aria-label="Sidebar control"
    >
      <span className={NAV_ICON_CONTAINER}>
        <PanelLeftDashed className={NAV_ICON_INACTIVE} />
      </span>
      {isExpanded && (
        <span className="flex flex-1 items-center justify-between whitespace-nowrap text-ink-3">
          <span>Sidebar</span>
          <kbd className="font-mono text-micro text-ink-3">⌘B</kbd>
        </span>
      )}
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent side="top" align={isExpanded ? 'start' : 'center'} sideOffset={8} className="w-52">
        <DropdownMenuLabel>Sidebar control</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={mode} onValueChange={(v) => onChangeMode(v as SidebarMode)}>
          {MODE_OPTIONS.map((opt) => (
            <DropdownMenuRadioItem key={opt.value} value={opt.value}>
              <opt.icon />
              {opt.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Section label ──────────────────────────────────────────────
// Expanded: a quiet sentence-case label. Collapsed: a hairline.

function SectionLabel({ title, isExpanded }: { title: string; isExpanded: boolean }) {
  if (!title) return <div className="mx-1.5 my-1.5 border-t border-sidebar-border" />;
  return (
    <div className="mt-3 flex h-7 items-center px-[11px]">
      {isExpanded ? (
        <span className="whitespace-nowrap text-micro font-medium text-ink-3">{title}</span>
      ) : (
        <div className="w-full border-t border-sidebar-border" />
      )}
    </div>
  );
}

// ─── Nav link ───────────────────────────────────────────────────
// Shared constants for all styling, so expanded and collapsed are identical
// except for text visibility.

function NavLink({
  item,
  isActive,
  isExpanded,
  nested = false,
}: {
  item: AppNavItem;
  isActive: boolean;
  isExpanded: boolean;
  nested?: boolean;
}) {
  const Icon = item.icon;
  const showBadge = item.badge !== undefined && item.badge > 0;

  const content = (
    <>
      {isActive && <span aria-hidden="true" className={ACTIVE_BAR} />}
      <span className={NAV_ICON_CONTAINER}>
        {Icon && <Icon className={isActive ? NAV_ICON_ACTIVE : NAV_ICON_INACTIVE} />}
        {showBadge && !isExpanded && (
          <span aria-hidden="true" className="absolute -right-1 -top-1 size-1.5 rounded-full bg-signal ring-2 ring-sidebar" />
        )}
      </span>
      {isExpanded && <span className="flex-1 truncate whitespace-nowrap font-medium">{item.title}</span>}
      {isExpanded && item.isExternal && item.newTab !== false && (
        <span aria-hidden="true" className="text-micro text-ink-3">
          ↗
        </span>
      )}
      {showBadge && isExpanded && (
        <span className="flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-xs bg-signal-soft px-1 font-mono text-micro font-medium text-signal-ink tnum">
          {item.badge}
        </span>
      )}
    </>
  );

  const label = showBadge ? `${item.title}, ${item.badge} open alerts` : item.title;
  const className = cn(
    NAV_LINK_BASE,
    isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
    !isExpanded && 'justify-center px-0',
    nested && isExpanded && 'pl-8',
  );

  const link = item.isExternal ? (
    <a
      href={item.href || '#'}
      target={item.newTab === false ? undefined : '_blank'}
      rel={item.newTab === false ? undefined : 'noopener noreferrer'}
      aria-label={label}
      className={className}
    >
      {content}
    </a>
  ) : (
    <Link to={item.href} aria-label={label} aria-current={isActive ? 'page' : undefined} className={className}>
      {content}
    </Link>
  );

  // Collapsed rail: the icon alone can't name the destination, so the label
  // rides a hover hint (Radix portals and flips it, so the rail can't clip it).
  if (isExpanded) return link;
  return (
    <SimpleTooltip content={item.title} side="right">
      {link}
    </SimpleTooltip>
  );
}

function MobileNavLink({
  item,
  isActive,
  onNavigate,
  nested = false,
}: {
  item: AppNavItem;
  isActive: boolean;
  onNavigate: () => void;
  nested?: boolean;
}) {
  const Icon = item.icon;
  const showBadge = item.badge !== undefined && item.badge > 0;
  const className = cn(
    'focus-ring relative flex h-10 items-center gap-3 rounded-sm px-3 text-sm font-medium transition-colors',
    nested && 'pl-10',
    isActive ? NAV_LINK_ACTIVE : NAV_LINK_INACTIVE,
  );
  const body = (
    <>
      {Icon && <Icon className={cn('size-4 shrink-0', isActive ? 'text-ink' : 'text-ink-3')} />}
      <span className="flex-1">{item.title}</span>
      {showBadge && (
        <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-xs bg-signal-soft px-1 font-mono text-micro text-signal-ink tnum">
          {item.badge}
        </span>
      )}
    </>
  );

  if (item.isExternal) {
    return (
      <a
        href={item.href || '#'}
        target={item.newTab === false ? undefined : '_blank'}
        rel={item.newTab === false ? undefined : 'noopener noreferrer'}
        onClick={onNavigate}
        className={className}
      >
        {body}
      </a>
    );
  }
  return (
    <Link to={item.href} aria-current={isActive ? 'page' : undefined} onClick={onNavigate} className={className}>
      {body}
    </Link>
  );
}
